import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellRing, ChevronDown, HelpCircle, LogOut, Menu, MessageSquarePlus,
  Moon, Search, ShieldCheck, Sliders, Sun, TriangleAlert, User as UserIcon, X,
} from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import FenetreRetour from './FenetreRetour';
import { annoncerErreur, Squelette } from '../etats';

const POLL_INTERVAL_MS = 25000;

/* « 2 h », « 5 j » : dans une liste, l'âge se saisit d'un coup d'œil là
   où une date complète demande un calcul à chaque ligne. La date entière
   reste dans l'infobulle, pour qui en a besoin. */
const depuisQuand = (valeur) => {
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return '';
  const minutes = Math.max(0, Math.round((Date.now() - d) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `${heures} h`;
  const jours = Math.round(heures / 24);
  return jours < 31 ? `${jours} j` : `${Math.round(jours / 30)} mois`;
};

export default function Header({ user, toggleSidebar, isSidebarOpen, onProfileClick, theme, onSetTheme, onLogout }) {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const menuCompteRef = useRef(null);
  const [menuCompte, setMenuCompte] = useState(false);
  // « probleme » ou « suggestion » : la fenêtre de retour ouverte, sinon null.
  const [retour, setRetour] = useState(null);

  // Naviguer FERME le menu : le laisser ouvert par-dessus la nouvelle page
  // donnerait l'impression que le clic n'a pas abouti.
  const naviguer = (chemin) => { setMenuCompte(false); navigate(chemin); };

  /* « Systeme » est un troisieme choix de preference, pas un troisieme jeu de
     couleurs : ce qui s'affiche reste clair ou sombre. La bascule doit donc
     lire l'apparence EFFECTIVE, sinon elle proposerait « mode sombre » a
     quelqu'un qui est deja en sombre par son appareil. */
  const sombre = theme === 'dark' || theme === 'forest'
    || (theme === 'system'
        && typeof window !== 'undefined'
        && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  // `notificationsChargees` : au moins une réponse reçue. Avant, ni « Tout
  // est lu » ni « Aucune demande » : on ne le sait pas encore.
  const [notificationsChargees, setNotificationsChargees] = useState(false);
  const [erreurNotifications, setErreurNotifications] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await adminApi.getAdminNotifications(user.id, 20);
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setUnreadCount(Number(data?.unread_count) || 0);
      setNotificationsChargees(true);
      setErreurNotifications(false);
    } catch (error) {
      // Le relevé périodique garde la liste affichée ; l'échec ne se dit
      // que s'il n'y a encore rien à montrer.
      setErreurNotifications(true);
    }
  }, [user?.id]);

  const markNotificationAsRead = useCallback(async (notificationId) => {
    if (!user?.id || !notificationId) return;
    try {
      await adminApi.markAdminNotificationsRead({
        adminId: user.id,
        notificationId,
      });
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  }, [user?.id]);

  const handleMarkAllRead = async () => {
    if (!user?.id || unreadCount === 0) return;
    try {
      await adminApi.markAdminNotificationsRead({
        adminId: user.id,
        markAll: true,
      });
      await loadNotifications();
    } catch (error) {
      annoncerErreur(error?.message || 'Les notifications n’ont pas pu être marquées comme lues.');
    }
  };

  const handleNotificationClick = async (item) => {
    if (!item) return;
    if (!item.is_read) {
      await markNotificationAsRead(item.id);
    }

    setIsNotificationOpen(false);
    await loadNotifications();

    // La demande elle-même, avec les informations du demandeur ; la fiche
    // de l'organisation seulement pour les anciennes demandes sans fiche.
    if (item.demande_id) {
      navigate(`/demandes?demande=${item.demande_id}`);
    } else if (item.organisation?.id) {
      navigate(`/users/${item.organisation.id}`);
    }
  };

  /* Un menu qui ne se referme qu'en cliquant de nouveau sur son bouton est un
     menu qu'on laisse ouvert par mégarde, et qui masque le contenu. */
  useEffect(() => {
    if (!menuCompte) return undefined;
    const auClic = (e) => {
      if (menuCompteRef.current && !menuCompteRef.current.contains(e.target)) {
        setMenuCompte(false);
      }
    };
    const auClavier = (e) => { if (e.key === 'Escape') setMenuCompte(false); };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [menuCompte]);

  useEffect(() => {
    loadNotifications();
    const intervalId = setInterval(() => loadNotifications(), POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadNotifications]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    /* ── La barre du haut du modèle v3 ────────────────────────────────
       Ce n'est plus un bandeau collé au bord de l'écran : c'est une CARTE
       arrondie, posée sur le fond du panneau, au-dessus d'une seconde
       carte qui porte la page. Le modèle la fixe à 22 px du haut, d'où le
       `top-[10px]` une fois retirée la marge propre du panneau.

       Le filet inférieur et le flou d'arrière-plan disparaissent : avec
       deux cartes distinctes, c'est le vide entre elles qui sépare. */
    <header className="shrink-0 z-30 flex items-center gap-2.5 min-h-[68px]
                       px-3.5 py-2.5 rounded-carte bg-[var(--bg-main)]">
      <button
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? 'Replier la navigation' : 'Ouvrir la navigation'}
        className="act act-icone act-contour lg:hidden"
      >
        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* La recherche est un LABEL : cliquer n'importe où dans la pilule —
          la loupe comprise — donne le focus au champ. */}
      <label className="recherche flex-[1_1_120px] max-w-[380px]">
        <Search className="w-[18px] h-[18px]" strokeWidth={1.8} />
        <input
          type="search"
          aria-label="Rechercher"
          placeholder="Rechercher une organisation, un projet…"
        />
        <span aria-hidden="true"
              className="hidden md:grid place-items-center h-[26px] px-2 shrink-0 rounded-[8px]
                         bg-[var(--bg-main)] text-[var(--text-muted)] text-[12px] font-semibold">
          Ctrl K
        </span>
      </label>

      <span className="flex-1" />

      {/* Aucune pastille « tout va bien » : elle occupait de la place en
          permanence pour dire ce qui est vrai 99 % du temps, et personne
          ne la lit. Quand quelque chose ne va pas, c'est une alerte qui
          le dit — voir `BandeauIncident` dans AdminLayout. */}

      {onSetTheme && (
        <button
          type="button"
          onClick={() => onSetTheme(sombre ? 'light' : 'dark')}
          aria-label={sombre ? "Passer en mode clair" : "Passer en mode sombre"}
          title={sombre ? "Mode clair" : "Mode sombre"}
          className="act act-icone act-contour"
        >
          {sombre
            ? <Sun className="w-5 h-5" aria-hidden="true" />
            : <Moon className="w-5 h-5" aria-hidden="true" />}
        </button>
      )}

      <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsNotificationOpen((prev) => !prev)}
            className="act act-icone act-tertiaire relative"
            aria-label="Notifications admin"
          >
            {unreadCount > 0 ? (
              <BellRing className="w-5 h-5 text-[var(--accent-texte)]" />
            ) : (
              <Bell className="w-5 h-5" />
            )}

            {unreadCount > 0 && (
              <>
                <span style={{ backgroundColor: 'var(--accent)', color: '#0A1128' }}
                  className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-controle text-[11px] font-semibold flex items-center justify-center tabular-nums">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
                
              </>
            )}
          </button>

          {isNotificationOpen && (
            <div className="absolute right-0 mt-3 w-[min(92vw,380px)] bg-[var(--bg-card)] shadow-carte rounded-pastille shadow-[0_18px_55px_rgba(12,30,45,0.2)] overflow-hidden z-50">
              {/* `bg-white/60` etait fige : en theme sombre, l'en-tete
                  restait blanc sur une carte sombre. */}
              <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-main)] flex items-center justify-between gap-4">
                <div>
                  <p className="text-[14px] font-semibold text-[var(--text-bold)]">
                    Notifications
                  </p>
                  <p className="text-[12.5px] text-[var(--text-muted)] mt-0.5">
                    {!notificationsChargees
                      ? (erreurNotifications ? 'Indisponibles pour l’instant' : 'Lecture…')
                      : unreadCount > 0
                        ? `${unreadCount} demande${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
                        : 'Tout est lu'}
                  </p>
                </div>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    
                    className="act act-tertiaire"
                  >
                    Tout marquer lu
                  </button>
                )}
              </div>

              <div className="max-h-[420px] overflow-y-auto">
                {!notificationsChargees && !erreurNotifications && (
                  <ul aria-label="Chargement des notifications" role="status">
                    {[0, 1, 2].map((i) => (
                      <li key={i} className="px-3 py-2.5 flex items-center gap-3">
                        <Squelette className="w-10 h-10 shrink-0" />
                        <span className="flex-1 min-w-0">
                          <Squelette className="h-3 mb-2" style={{ width: `${50 + i * 12}%` }} />
                          <Squelette className="h-2.5" style={{ width: '70%' }} />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {!notificationsChargees && erreurNotifications && (
                  <div className="px-4 py-6 text-center" role="alert">
                    <p className="text-xs font-semibold text-[var(--text-muted)]">
                      Les notifications n’ont pas pu être chargées.
                    </p>
                    <button type="button" onClick={() => loadNotifications()}
                            className="act act-sm act-contour mt-2">
                      Réessayer
                    </button>
                  </div>
                )}

                {notificationsChargees && notifications.length === 0 && (
                  <div className="px-4 py-6 text-center">
                    <p className="text-xs font-semibold text-[var(--text-muted)]">
                      Aucune demande de contact expert pour le moment.
                    </p>
                  </div>
                )}

                {notifications.map((item) => {
                  const nom = item.organisation?.name || 'Organisation';
                  return (
                    /* ── Une notification, deux lignes ─────────────────
                       Elle en occupait quatre : le nom, le message, le
                       type et l'adresse, puis la date. Dans un panneau
                       de 380 px, trois notifications remplissaient
                       l'écran — et le type d'organisation n'a jamais
                       aidé personne à décider s'il fallait cliquer.

                       Restent le nom, l'amorce du message et l'âge. Le
                       reste est sur la fiche, que ce clic ouvre. */
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleNotificationClick(item)}
                      className="w-full text-left px-3 py-2.5 flex items-center gap-3
                                 transition-colors relative hover:bg-[var(--bg-main)]
                                 after:absolute after:left-[60px] after:right-0 after:bottom-0
                                 after:h-px after:bg-[var(--border)] last:after:hidden"
                    >
                      <span className={`w-10 h-10 shrink-0 rounded-controle grid place-items-center
                                        text-[13px] font-bold ${item.is_read
                                          ? 'bg-[var(--info-fond)] text-[var(--text-bold)]'
                                          : 'bg-[var(--vert-pale)] text-[var(--vert-fonce)]'}`}>
                        {nom.slice(0, 2).toUpperCase()}
                      </span>

                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`truncate text-[14px] ${item.is_read
                            ? 'font-medium text-[var(--text-bold)]'
                            : 'font-semibold text-[var(--text-bold)]'}`}>
                            {nom}
                          </span>
                          <span className={`text-[12px] tabular-nums shrink-0 ${item.is_read
                            ? 'text-[var(--text-muted)]'
                            : 'text-[var(--vert-fonce)] font-semibold'}`}>
                            {depuisQuand(item.created_at)}
                          </span>
                        </span>
                        <span className="flex items-center gap-1.5 mt-0.5">
                          <span className="flex-1 min-w-0 truncate text-[13px] text-[var(--text-muted)]">
                            {item.comment || 'Demande de contact'}
                          </span>
                          {!item.is_read && (
                            <span aria-label="Non lue"
                                  className="w-2 h-2 shrink-0 rounded-controle bg-[var(--accent)]" />
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Menu du compte ──────────────────────────────────────────────
            L'avatar menait directement aux paramètres. C'était court pour
            « changer de thème », impossible pour « me déconnecter » — qui
            n'existait qu'en bas de la barre latérale, donc nulle part quand
            elle est réduite ou en tiroir.

            Les entrées vont du personnel au général, et la déconnexion est
            seule sous un filet : c'est la seule dont on ne revient pas. */}
        <div className="relative pl-1" ref={menuCompteRef}>
          <button
            onClick={() => setMenuCompte((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuCompte}
            className="flex items-center gap-2.5 outline-none rounded-controle
                       focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          >
            {/* Le double anneau du modèle — blanc puis gris — détache la
                pastille du fond sans lui donner de bordure. */}
            <span className="w-11 h-11 shrink-0 rounded-controle grid place-items-center
                             bg-[var(--nuit)] text-[var(--accent)] text-[14px] font-bold
                             shadow-[0_0_0_3px_var(--bg-main),0_0_0_4px_var(--border)]">
              {user.username.slice(0, 2).toUpperCase()}
            </span>
            <span className="hidden xl:flex flex-col leading-[1.25] text-left">
              <span className="text-[14px] font-semibold text-[var(--text-bold)]">{user.username}</span>
              <span className="text-[12px] text-[var(--text-muted)]">{user.role}</span>
            </span>
            <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-[var(--text-muted)] transition-transform ${menuCompte ? 'rotate-180' : ''}`}
                         aria-hidden="true" />
          </button>

          {menuCompte && (
            <div role="menu"
                 className="absolute right-0 mt-2 w-60 rounded-carte shadow-carte bg-[var(--bg-card)] shadow-xl overflow-hidden z-50 py-1">
              <div className="px-4 py-3 border-b border-[var(--border)] sm:hidden">
                <p className="text-[13px] font-medium text-[var(--text-bold)] truncate">{user.username}</p>
                <p className="text-[11px] text-[var(--text-muted)]">{user.role}</p>
              </div>

              {[
                { icone: UserIcon, libelle: 'Mon profil', aller: () => naviguer('/profile') },
                { icone: Sliders, libelle: 'Paramètres', aller: () => naviguer('/settings') },
                { icone: ShieldCheck, libelle: 'Mot de passe', aller: () => naviguer('/profile#mot-de-passe') },
                { icone: sombre ? Sun : Moon,
                  libelle: sombre ? 'Thème clair' : 'Thème sombre',
                  aller: () => onSetTheme(sombre ? 'light' : 'dark'), garder: true },
                { separateur: true },
                { icone: HelpCircle, libelle: 'Aide et support', aller: () => naviguer('/help') },
                // Au premier plan, sur la page en cours : on parle de l'écran
                // qu'on a sous les yeux, et il reste là, dessous.
                { icone: TriangleAlert, libelle: 'Signaler un problème',
                  aller: () => { setMenuCompte(false); setRetour('probleme'); } },
                { icone: MessageSquarePlus, libelle: 'Commentaires et suggestions',
                  aller: () => { setMenuCompte(false); setRetour('suggestion'); } },
                { separateur: true },
                { icone: LogOut, libelle: 'Déconnexion', aller: () => onLogout?.(), rouge: true },
              ].map((entree, i) => (
                entree.separateur ? (
                  <div key={`sep-${i}`} className="my-1 border-t border-[var(--border)]" />
                ) : (
                  <button key={entree.libelle} role="menuitem" type="button"
                          onClick={() => { entree.aller(); if (!entree.garder) setMenuCompte(false); }}
                          className={`w-full text-left px-4 py-2.5 text-[13px] flex items-center gap-2.5
                                      hover:bg-[var(--bg-main)] transition-colors ${
                                        entree.rouge ? 'text-[var(--alerte)]' : 'text-[var(--text-main)]'}`}>
                    <entree.icone className="w-4 h-4 shrink-0" aria-hidden="true" />
                    {entree.libelle}
                  </button>
                )
              ))}
            </div>
          )}
        </div>
      {retour && <FenetreRetour nature={retour} onClose={() => setRetour(null)} />}
    </header>
  );
}
